import React, { useState, useEffect, useRef, useCallback } from 'react';
import { apiFetch } from '../../api';

const EU_COUNTRIES = [
  { code: 'AUT', name: 'Austria' },       { code: 'BEL', name: 'Belgium' },
  { code: 'BGR', name: 'Bulgaria' },      { code: 'CYP', name: 'Cyprus' },
  { code: 'CZE', name: 'Czech Republic' },{ code: 'DEU', name: 'Germany' },
  { code: 'DNK', name: 'Denmark' },       { code: 'EST', name: 'Estonia' },
  { code: 'ESP', name: 'Spain' },         { code: 'FIN', name: 'Finland' },
  { code: 'FRA', name: 'France' },        { code: 'GRC', name: 'Greece' },
  { code: 'HRV', name: 'Croatia' },       { code: 'HUN', name: 'Hungary' },
  { code: 'IRL', name: 'Ireland' },       { code: 'ISL', name: 'Iceland' },
  { code: 'ITA', name: 'Italy' },         { code: 'LIE', name: 'Liechtenstein' },
  { code: 'LTU', name: 'Lithuania' },     { code: 'LUX', name: 'Luxembourg' },
  { code: 'LVA', name: 'Latvia' },        { code: 'MLT', name: 'Malta' },
  { code: 'NLD', name: 'Netherlands' },   { code: 'NOR', name: 'Norway' },
  { code: 'POL', name: 'Poland' },        { code: 'PRT', name: 'Portugal' },
  { code: 'ROU', name: 'Romania' },       { code: 'SWE', name: 'Sweden' },
  { code: 'SVN', name: 'Slovenia' },      { code: 'SVK', name: 'Slovakia' },
  { code: 'GBR', name: 'United Kingdom' },
];

const POLL_INTERVAL = 3000; // only used while a pipeline is running

const FEED_TYPE = {
  ingestion: { bg: '#dbeafe', color: '#1d4ed8', label: 'Ingestion' },
  fanout:    { bg: '#fef9c3', color: '#854d0e', label: 'Fan-out' },
  embedding: { bg: '#f3e8ff', color: '#7c3aed', label: 'Embedding' },
  verdict:   { bg: '#dcfce7', color: '#15803d', label: 'Verdicts' },
};

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-GB', {
    day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
  });
}

function timeSince(isoOrDate) {
  if (!isoOrDate) return '—';
  const d = isoOrDate instanceof Date
    ? isoOrDate
    : new Date(typeof isoOrDate === 'string' && !isoOrDate.endsWith('Z') ? isoOrDate + 'Z' : isoOrDate);
  const s = Math.max(0, Math.round((Date.now() - d) / 1000));
  if (s < 15)    return 'just now';
  if (s < 60)    return `${s}s ago`;
  if (s < 3600)  return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

function duration(start, end) {
  if (!start) return '—';
  const ms = new Date(end || Date.now()) - new Date(start);
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s`;
  return `${Math.floor(s / 60)}m ${s % 60}s`;
}

function pct(progress) {
  if (!progress) return 0;
  const { processed, total, ranked } = progress;
  const done = processed ?? ranked ?? 0;
  if (!total) return 0;
  return Math.round((done / total) * 100);
}

function stepLabel(job) {
  if (!job) return '';
  if (job.status === 'completed') return 'Done';
  if (job.status === 'failed')    return 'Failed';
  const p = job.progress || {};
  const step = p.step || '';
  if (step === 'fetching')    return `Fetching page ${p.page || ''}…`;
  if (step === 'translating') return `Translating page ${p.page || ''}…`;
  if (step === 'embedding')   return 'Embedding…';
  if (step === 'running')     return `${p.processed ?? p.ranked ?? 0} / ${p.total ?? '?'}`;
  if (step === 'done')        return 'Done';
  return step || 'Running…';
}

function buildFeed(runs) {
  return [
    ...runs.ingestion.map(r => ({
      ...r, _type: 'ingestion',
      _metric: r.tenders_fetched != null
        ? `${r.tenders_fetched} fetched, ${r.tenders_embedded ?? 0} embedded` : null,
    })),
    ...runs.fanout.map(r => ({
      ...r, _type: 'fanout',
      _metric: r.matches_created != null ? `${r.matches_created} matches created` : null,
    })),
    ...runs.embedding.map(r => ({
      ...r, _type: 'embedding',
      _metric: r.profiles_processed != null
        ? `${r.profiles_processed} profiles, ${r.variants_created ?? 0} variants` : null,
    })),
    ...runs.verdict.map(r => ({
      ...r, _type: 'verdict',
      _metric: r.verdicts_written != null
        ? `${r.verdicts_written} verdicted, ${r.cache_hits ?? 0} cached` : null,
    })),
  ].sort((a, b) => new Date(b.started_at) - new Date(a.started_at)).slice(0, 30);
}

// Build the query params shared by the verdict count + run endpoints.
// Scores are shown 0–100 in the UI but sent as 0–1 cosine. Defaults
// (min 50 / max 100 / no cap) are omitted so a full-range run still means
// "evaluate everything", matching the original behaviour.
function buildVerdictParams(band, topN) {
  const p = new URLSearchParams();
  if (band.min > 50)  p.append('min_score', (band.min / 100).toFixed(2));
  if (band.max < 100) p.append('max_score', (band.max / 100).toFixed(2));
  const n = Number(topN);
  if (n > 0) p.append('top_n_per_profile', String(n));
  return p;
}

// ── Styles ────────────────────────────────────────────────────────────────────

const c = {
  page:      { maxWidth: 1100, margin: '32px auto', padding: '0 24px 60px', fontFamily: 'system-ui, -apple-system, sans-serif' },
  headerRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 },
  heading:   { fontSize: 24, fontWeight: 700, color: '#0f172a', margin: 0 },
  liveRow:   { display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#64748b' },
  liveDot:   { width: 8, height: 8, borderRadius: '50%', background: '#22c55e', flexShrink: 0 },
  sub:       { fontSize: 14, color: '#64748b', marginBottom: 24, marginTop: 4 },

  statsBar:  { display: 'flex', gap: 10, marginBottom: 20, flexWrap: 'wrap' },
  stat:      { flex: '1 1 120px', background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, padding: '10px 14px' },
  statLabel: { fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em' },
  statVal:   { fontSize: 20, fontWeight: 700, color: '#0f172a', marginTop: 2 },

  flowStrip: {
    display: 'flex', alignItems: 'center', gap: 4,
    background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10,
    padding: '12px 20px', marginBottom: 20, overflowX: 'auto',
  },
  flowStep: (s) => ({
    display: 'flex', alignItems: 'center', gap: 7,
    fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap',
    color: s === 'running' ? '#1d4ed8' : s === 'completed' ? '#15803d' : s === 'failed' ? '#b91c1c' : '#64748b',
  }),
  flowDot: (s) => ({
    width: 9, height: 9, borderRadius: '50%', flexShrink: 0,
    background: s === 'running' ? '#3b82f6' : s === 'completed' ? '#22c55e' : s === 'failed' ? '#ef4444' : '#cbd5e1',
  }),
  flowArrow:   { color: '#cbd5e1', fontSize: 18, fontWeight: 300, margin: '0 10px', flexShrink: 0 },
  flexSpacer:  { flex: 1, minWidth: 16 },

  grid:       { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(460px, 1fr))', gap: 18, marginBottom: 32 },
  card:       { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 14, padding: '20px 24px' },
  cardHeader: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 12 },
  cardNum:    { fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 2 },
  cardTitle:  { fontSize: 16, fontWeight: 700, color: '#0f172a' },
  cardDesc:   { fontSize: 12, color: '#64748b', marginTop: 2 },
  badge:      (s) => ({
    fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 20, whiteSpace: 'nowrap',
    background: s === 'running' ? '#dbeafe' : s === 'completed' ? '#dcfce7' : s === 'failed' ? '#fee2e2' : '#f1f5f9',
    color:      s === 'running' ? '#1d4ed8' : s === 'completed' ? '#15803d' : s === 'failed' ? '#b91c1c' : '#64748b',
  }),

  metricBig:     { fontSize: 28, fontWeight: 800, color: '#0f172a', lineHeight: 1 },
  metricSub:     { fontSize: 12, color: '#64748b', marginTop: 2, marginBottom: 10 },
  progressWrap:  { background: '#f1f5f9', borderRadius: 6, height: 8, margin: '8px 0 4px', overflow: 'hidden' },
  progressBar:   (p, s) => ({
    height: '100%', borderRadius: 6, transition: 'width 0.4s ease',
    width: `${Math.min(100, p)}%`,
    background: s === 'failed' ? '#ef4444' : s === 'completed' ? '#22c55e' : '#3b82f6',
  }),
  progressLabel: { fontSize: 12, color: '#64748b', marginBottom: 2 },
  lastRun:       { fontSize: 12, color: '#94a3b8', marginBottom: 10 },
  divider:       { height: 1, background: '#f1f5f9', margin: '12px 0' },

  configRow:   { display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 10 },
  configLabel: { fontSize: 12, fontWeight: 600, color: '#374151', minWidth: 72 },
  numInput:    { padding: '5px 8px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13, width: 64, textAlign: 'center' },
  hint:        { fontSize: 12, color: '#94a3b8' },

  sliderRow:   { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 },
  sliderTag:   { fontSize: 11, fontWeight: 700, color: '#64748b', width: 34, flexShrink: 0 },
  sliderVal:   { fontSize: 13, fontWeight: 700, color: '#0f172a', width: 30, textAlign: 'right', flexShrink: 0, fontVariantNumeric: 'tabular-nums' },
  range:       { flex: 1, accentColor: '#2563eb', cursor: 'pointer', height: 4 },
  countLine:   { fontSize: 13, color: '#374151', marginTop: 8 },

  countryGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 5, marginTop: 6, marginBottom: 6 },
  chip:        (sel) => ({
    display: 'flex', alignItems: 'center', gap: 5, padding: '4px 8px', borderRadius: 5,
    cursor: 'pointer', userSelect: 'none', fontSize: 11,
    fontWeight: sel ? 600 : 400,
    background: sel ? '#dbeafe' : '#f8fafc',
    color:      sel ? '#1d4ed8' : '#374151',
    border:    `1px solid ${sel ? '#93c5fd' : '#e2e8f0'}`,
  }),
  textBtn: { fontSize: 12, color: '#2563eb', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 500, padding: 0, textDecoration: 'underline' },
  tedResult: (ok) => ({
    display: 'inline-flex', alignItems: 'center', gap: 6,
    fontSize: 12, fontWeight: 500, borderRadius: 6, padding: '4px 10px',
    color:      ok ? '#15803d' : '#b91c1c',
    background: ok ? '#f0fdf4' : '#fef2f2',
    border:    `1px solid ${ok ? '#bbf7d0' : '#fecaca'}`,
  }),

  actionRow: { display: 'flex', gap: 10, alignItems: 'center', marginTop: 10, flexWrap: 'wrap' },
  btn: (v, d) => {
    const base = { border: 'none', fontWeight: 600, fontSize: 13, padding: '7px 16px', borderRadius: 7, cursor: d ? 'not-allowed' : 'pointer' };
    if (v === 'primary')   return { ...base, background: d ? '#93c5fd' : '#2563eb', color: '#fff' };
    if (v === 'secondary') return { ...base, background: '#f1f5f9', color: '#374151' };
    if (v === 'outline')   return { ...base, background: '#fff', color: '#2563eb', border: '1px solid #93c5fd' };
    if (v === 'green')     return { ...base, background: d ? '#86efac' : '#16a34a', color: '#fff', fontSize: 12, padding: '5px 14px' };
    return base;
  },
  msgText: (ok) => ({ fontSize: 12, fontWeight: 500, color: ok ? '#16a34a' : '#b91c1c' }),

  feedBox:   { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 14, overflow: 'hidden' },
  feedHead:  { padding: '14px 20px', borderBottom: '1px solid #f1f5f9', fontSize: 13, fontWeight: 700, color: '#374151', display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  feedTable: { width: '100%', borderCollapse: 'collapse', fontSize: 12 },
  feedTh:    { color: '#94a3b8', fontWeight: 700, padding: '8px 16px', textAlign: 'left', borderBottom: '1px solid #f1f5f9', background: '#f8fafc', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em' },
  feedTd:    { color: '#374151', padding: '9px 16px', borderBottom: '1px solid #f8fafc' },
};

// ── DroppedVerdictsPanel ──────────────────────────────────────────────────────

function DroppedVerdictsPanel({ count }) {
  const [open, setOpen]       = useState(false);
  const [rows, setRows]       = useState(null);
  const [loading, setLoading] = useState(false);
  const [profileFilter, setProfileFilter] = useState('');

  async function load() {
    setLoading(true);
    try {
      const url = '/admin/dropped-verdicts?limit=200' + (profileFilter ? `&profile_id=${profileFilter}` : '');
      const data = await apiFetch(url);
      setRows(data || []);
    } finally {
      setLoading(false);
    }
  }

  function toggle() {
    if (!open && rows === null) load();
    setOpen(o => !o);
  }

  const profiles = rows ? [...new Map(rows.map(r => [r.profile_id, r.profile_name])).entries()] : [];
  const filtered = rows
    ? (profileFilter ? rows.filter(r => r.profile_id === profileFilter) : rows)
    : [];

  return (
    <div style={{ ...c.feedBox, marginBottom: 24 }}>
      <div style={{ ...c.feedHead, cursor: 'pointer' }} onClick={toggle}>
        <span>
          Dropped by Claude
          <span style={{ marginLeft: 8, background: '#ffedd5', color: '#c2410c', fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 20 }}>
            {count.toLocaleString()}
          </span>
        </span>
        <span style={{ fontSize: 12, color: '#94a3b8' }}>
          {open ? 'Hide ▲' : 'Review ▼'}
        </span>
      </div>

      {open && (
        <div>
          {profiles.length > 1 && (
            <div style={{ padding: '8px 16px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 12, color: '#64748b' }}>Profile:</span>
              <select
                style={{ fontSize: 12, border: '1px solid #e2e8f0', borderRadius: 6, padding: '3px 8px' }}
                value={profileFilter}
                onChange={e => setProfileFilter(e.target.value)}
              >
                <option value=''>All profiles</option>
                {profiles.map(([id, name]) => <option key={id} value={id}>{name || id}</option>)}
              </select>
            </div>
          )}
          {loading && <div style={{ padding: 16, fontSize: 13, color: '#94a3b8' }}>Loading…</div>}
          {!loading && filtered.length === 0 && (
            <div style={{ padding: 16, fontSize: 13, color: '#94a3b8' }}>No dropped verdicts found.</div>
          )}
          {!loading && filtered.length > 0 && (
            <div style={{ overflowX: 'auto' }}>
              <table style={c.feedTable}>
                <thead>
                  <tr>
                    {['Score', 'Profile', 'Title', 'Country', 'CPV', 'Size', 'Deadline', 'Description'].map(h => (
                      <th key={h} style={c.feedTh}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((r, i) => (
                    <tr key={i} style={{ background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                      <td style={{ ...c.feedTd, fontWeight: 700, color: r.cosine_score >= 50 ? '#15803d' : r.cosine_score >= 38 ? '#854d0e' : '#94a3b8', whiteSpace: 'nowrap' }}>
                        {r.cosine_score}
                      </td>
                      <td style={{ ...c.feedTd, whiteSpace: 'nowrap', maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.profile_name}</td>
                      <td style={{ ...c.feedTd, maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.title}</td>
                      <td style={{ ...c.feedTd, whiteSpace: 'nowrap' }}>{r.country}</td>
                      <td style={{ ...c.feedTd, fontSize: 11, color: '#64748b', whiteSpace: 'nowrap' }}>{(r.cpv_codes || []).join(', ')}</td>
                      <td style={{ ...c.feedTd, whiteSpace: 'nowrap' }}>{r.contract_size}</td>
                      <td style={{ ...c.feedTd, whiteSpace: 'nowrap' }}>{r.deadline}</td>
                      <td style={{ ...c.feedTd, maxWidth: 300, fontSize: 11, color: '#64748b' }}>{r.description}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}


// ── PipelineCard ──────────────────────────────────────────────────────────────

function PipelineCard({ number, title, description, job, pendingCount, countLabel, onRun, runLabel, message, children }) {
  const status    = job?.status || 'idle';
  const isRunning = status === 'running';
  const progress  = pct(job?.progress);
  const btnDisabled = isRunning || (pendingCount != null && pendingCount === 0);

  return (
    <div style={c.card}>
      <div style={c.cardHeader}>
        <div>
          <div style={c.cardNum}>{number}</div>
          <div style={c.cardTitle}>{title}</div>
          <div style={c.cardDesc}>{description}</div>
        </div>
        <span style={c.badge(status)}>{status}</span>
      </div>

      {pendingCount != null && (
        <>
          <div style={c.metricBig}>{pendingCount.toLocaleString()}</div>
          <div style={c.metricSub}>{countLabel}</div>
        </>
      )}

      {(isRunning || status === 'completed' || status === 'failed') && (
        <>
          <div style={c.progressLabel}>{stepLabel(job)}</div>
          <div style={c.progressWrap}>
            <div style={c.progressBar(status === 'completed' ? 100 : progress, status)} />
          </div>
        </>
      )}

      {job && (
        <div style={c.lastRun}>
          Last run {timeSince(job.started_at)}
          {job.completed_at && ` · ${duration(job.started_at, job.completed_at)}`}
        </div>
      )}

      {children && (
        <>
          <div style={c.divider} />
          {children}
        </>
      )}

      <div style={c.actionRow}>
        <button
          style={c.btn('primary', btnDisabled)}
          onClick={onRun}
          disabled={btnDisabled}
        >
          {isRunning ? 'Running…' : runLabel}
        </button>
        {message && <span style={c.msgText(message.ok)}>{message.text}</span>}
      </div>
    </div>
  );
}

// ── Activity Feed ─────────────────────────────────────────────────────────────

function ActivityFeed({ runs }) {
  const items = buildFeed(runs);

  return (
    <div style={c.feedBox}>
      <div style={c.feedHead}>
        <span>Recent Activity</span>
        {items.length > 0 && (
          <span style={{ fontSize: 12, fontWeight: 400, color: '#94a3b8' }}>{items.length} runs</span>
        )}
      </div>
      {items.length === 0 ? (
        <div style={{ padding: '24px 20px', fontSize: 13, color: '#94a3b8', textAlign: 'center' }}>
          No pipeline runs yet.
        </div>
      ) : (
        <table style={c.feedTable}>
          <thead>
            <tr>
              <th style={c.feedTh}>Pipeline</th>
              <th style={c.feedTh}>Started</th>
              <th style={c.feedTh}>Status</th>
              <th style={c.feedTh}>Result</th>
              <th style={c.feedTh}>Duration</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item, i) => {
              const { bg, color, label } = FEED_TYPE[item._type] || { bg: '#f1f5f9', color: '#64748b', label: item._type };
              return (
                <tr key={`${item._type}-${item.id ?? i}`}>
                  <td style={c.feedTd}>
                    <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 20, background: bg, color }}>
                      {label}
                    </span>
                  </td>
                  <td style={c.feedTd}>{formatDate(item.started_at)}</td>
                  <td style={c.feedTd}>
                    <span style={{
                      fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 20,
                      background: item.status === 'completed' ? '#dcfce7' : item.status === 'running' ? '#dbeafe' : '#fee2e2',
                      color:      item.status === 'completed' ? '#15803d' : item.status === 'running' ? '#1d4ed8' : '#b91c1c',
                    }}>
                      {item.status}
                    </span>
                  </td>
                  <td style={c.feedTd}>{item._metric ?? '—'}</td>
                  <td style={c.feedTd}>{duration(item.started_at, item.completed_at)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ── Main ──────────────────────────────────────────────────────────────────────

export default function AdminPipelines() {
  const [jobs, setJobs]               = useState(null);
  const [runs, setRuns]               = useState({ ingestion: [], fanout: [], verdict: [], embedding: [] });
  const [lastRefresh, setLastRefresh] = useState(null);
  const [msgs, setMsgs]               = useState({});
  const [cleanupMsg, setCleanupMsg]   = useState(null);
  const [cleanupBusy, setCleanupBusy] = useState(false);
  const [orphanCount, setOrphanCount] = useState(null);
  const [orphanMsg, setOrphanMsg]     = useState(null);
  const [orphanBusy, setOrphanBusy]   = useState(false);
  const [digestMsg, setDigestMsg]     = useState(null);
  const [digestBusy, setDigestBusy]   = useState(null); // holds 'daily' | 'weekly' while sending
  const [resummBusy, setResummBusy]   = useState(false);
  const [resummMsg, setResummMsg]     = useState(null);
  const [resummLeft, setResummLeft]   = useState(null); // live remaining count while backfilling
  const [rematchBusy, setRematchBusy] = useState(false);
  const [rematchMsg, setRematchMsg]   = useState(null);
  const [reembedBusy, setReembedBusy] = useState(false);
  const [reembedMsg, setReembedMsg]   = useState(null);
  const [calibBusy, setCalibBusy]     = useState(false);
  const [calibMsg, setCalibMsg]       = useState(null);
  const [healthBusy, setHealthBusy]   = useState(false);
  const [health, setHealth]           = useState(null);
  const [healthErr, setHealthErr]     = useState(null);
  const [promoPreview, setPromoPreview] = useState(null);
  const [promoBusy, setPromoBusy]     = useState(false);
  const [promoMsg, setPromoMsg]       = useState(null);
  const [famBusy, setFamBusy]         = useState(false);
  const [famMsg, setFamMsg]           = useState(null);
  const [famResult, setFamResult]     = useState(null);
  const [cpvBusy, setCpvBusy]         = useState(false);
  const [cpvMsg, setCpvMsg]           = useState(null);
  const [cpvStatus, setCpvStatus]     = useState(null); // {running, last_run}
  const [lexBusy, setLexBusy]         = useState(false);
  const [lexMsg, setLexMsg]           = useState(null);
  const [lexStatus, setLexStatus]     = useState(null); // {running, last_run, remaining}
  const pollRef                       = useRef(null);
  const resummPollRef                 = useRef(null);
  const reembedPollRef                = useRef(null);
  const cpvPollRef                    = useRef(null);
  const lexPollRef                    = useRef(null);
  const jobsRef                       = useRef(null);

  // Ingestion config
  const [daysBack, setDaysBack]                   = useState(1);
  const [maxTenders, setMaxTenders]               = useState(20);
  const [selectedCountries, setSelectedCountries] = useState(EU_COUNTRIES.map(x => x.code));
  const [countriesOpen, setCountriesOpen]         = useState(false);
  const [tedChecking, setTedChecking]             = useState(false);
  const [tedResult, setTedResult]                 = useState(null);

  // Verdict config — Pinecone score band + per-profile cap
  const [scoreBand, setScoreBand]       = useState({ min: 50, max: 100 });
  const [topN, setTopN]                 = useState('');
  const [bandCount, setBandCount]       = useState(null);
  const [countLoading, setCountLoading] = useState(false);

  const fetchJobs = useCallback(async () => {
    try {
      const data = await apiFetch('/admin/active-jobs');
      jobsRef.current = data;
      setJobs(data);
      setLastRefresh(new Date());
    } catch (e) {
      console.error('active-jobs poll error', e);
    }
  }, []);

  const fetchRuns = useCallback(async () => {
    try {
      const [ing, fo, vd, emb] = await Promise.all([
        apiFetch('/admin/ingestion'),
        apiFetch('/admin/fanout-runs'),
        apiFetch('/admin/verdict-runs'),
        apiFetch('/admin/profile-embedding-runs'),
      ]);
      setRuns({ ingestion: ing || [], fanout: fo || [], verdict: vd || [], embedding: emb || [] });
    } catch (e) {
      console.error('runs fetch error', e);
    }
  }, []);

  function anyRunning(data) {
    return ['ingestion', 'fanout', 'verdict', 'embedding'].some(
      k => data?.[k]?.status === 'running'
    );
  }

  function startPolling() {
    if (pollRef.current) return;
    pollRef.current = setInterval(async () => {
      const data = await apiFetch('/admin/active-jobs').catch(() => null);
      if (!data) return;
      jobsRef.current = data;
      setJobs(data);
      setLastRefresh(new Date());
      if (!anyRunning(data)) {
        clearInterval(pollRef.current);
        pollRef.current = null;
        fetchRuns();
      }
    }, POLL_INTERVAL);
  }

  useEffect(() => {
    fetchJobs().then(() => {
      if (anyRunning(jobsRef.current)) startPolling();
    });
    fetchRuns();
    fetchOrphanCount();
    return () => {
      clearInterval(pollRef.current); pollRef.current = null;
      clearInterval(resummPollRef.current); resummPollRef.current = null;
      clearInterval(reembedPollRef.current); reembedPollRef.current = null;
      clearInterval(cpvPollRef.current); cpvPollRef.current = null;
      clearInterval(lexPollRef.current); lexPollRef.current = null;
    };
  }, [fetchJobs, fetchRuns]); // eslint-disable-line

  async function trigger(key, endpoint, params = '') {
    setMsgs(m => ({ ...m, [key]: null }));
    try {
      const url = params ? `${endpoint}?${params}` : endpoint;
      const res = await apiFetch(url, { method: 'POST' });
      setMsgs(m => ({ ...m, [key]: { text: res.message, ok: true } }));
      await fetchJobs();
      startPolling();
    } catch (e) {
      setMsgs(m => ({ ...m, [key]: { text: e.message, ok: false } }));
    }
  }

  async function triggerIngestion() {
    const params = new URLSearchParams({ days_back: daysBack, max_tenders: maxTenders });
    if (selectedCountries.length < EU_COUNTRIES.length) selectedCountries.forEach(code => params.append('countries', code));
    await trigger('ingestion', '/admin/ingest', params.toString());
  }

  async function checkTed() {
    setTedChecking(true);
    setTedResult(null);
    try {
      const params = new URLSearchParams({ days_back: daysBack });
      if (selectedCountries.length < EU_COUNTRIES.length) selectedCountries.forEach(code => params.append('countries', code));
      const res = await apiFetch(`/admin/ted-available?${params}`);
      setTedResult({ total: res.total });
    } catch (e) {
      setTedResult({ error: e.message });
    } finally {
      setTedChecking(false);
    }
  }

  async function runCatchup() {
    await Promise.all([
      trigger('fanout',   '/admin/run-fanout'),
      trigger('verdict',  '/admin/run-verdicts'),
    ]);
  }

  const fetchVerdictCount = useCallback(async (band, n) => {
    setCountLoading(true);
    try {
      const params = buildVerdictParams(band, n);
      const data = await apiFetch(`/admin/verdict-count?${params.toString()}`);
      setBandCount(data?.count ?? 0);
    } catch (e) {
      console.error('verdict-count error', e);
    } finally {
      setCountLoading(false);
    }
  }, []);

  // Live verdict count — debounced so dragging the sliders doesn't spam the API.
  // Also fires on mount to populate the initial count.
  useEffect(() => {
    const t = setTimeout(() => fetchVerdictCount(scoreBand, topN), 250);
    return () => clearTimeout(t);
  }, [scoreBand, topN, fetchVerdictCount]);

  async function triggerVerdict() {
    const params = buildVerdictParams(scoreBand, topN).toString();
    await trigger('verdict', '/admin/run-verdicts', params);
    fetchVerdictCount(scoreBand, topN);
  }

  async function runCleanup() {
    setCleanupBusy(true);
    setCleanupMsg(null);
    try {
      const res = await apiFetch('/admin/cleanup-expired-tenders', { method: 'POST' });
      setCleanupMsg({ text: res.message, ok: true });
      fetchJobs();
    } catch (e) {
      setCleanupMsg({ text: e.message, ok: false });
    } finally {
      setCleanupBusy(false);
    }
  }

  async function fetchOrphanCount() {
    try {
      const data = await apiFetch('/admin/orphan-profile-vectors');
      setOrphanCount(data?.orphans ?? null);
    } catch (e) {
      console.error('orphan-profile-vectors error', e);
    }
  }

  async function runOrphanCleanup() {
    setOrphanBusy(true);
    setOrphanMsg(null);
    try {
      const res = await apiFetch('/admin/cleanup-orphan-profile-vectors', { method: 'POST' });
      setOrphanMsg({ text: res.message, ok: true });
      fetchOrphanCount();
    } catch (e) {
      setOrphanMsg({ text: e.message, ok: false });
    } finally {
      setOrphanBusy(false);
    }
  }

  async function sendTestDigest(frequency) {
    setDigestBusy(frequency);
    setDigestMsg(null);
    try {
      const res = await apiFetch(`/admin/send-test-digest?frequency=${frequency}`, { method: 'POST' });
      setDigestMsg({ text: res.message, ok: res.sent });
    } catch (e) {
      setDigestMsg({ text: e.message, ok: false });
    } finally {
      setDigestBusy(null);
    }
  }

  // Re-summarise, then auto-trigger fan-out so the refreshed vectors regenerate
  // matches. Two modes: targeted (retry what broke — small) and allActive (the
  // whole corpus, after a summariser-prompt change — one AI call per notice).
  //
  // Progress comes from the job's own in-process `progress` block, NOT from the
  // draining target count: an allActive sweep leaves every row's summary_status
  // alone, so the counts never fall and the old poll would have watched a number
  // that could not move. It also never declares success on timeout — the previous
  // version gave up after 4 minutes, started fan-out against a half-rewritten
  // corpus and reported "complete", which was survivable only while the target
  // set was tiny.
  async function runResummarise(allActive = false) {
    if (allActive) {
      let dry;
      try {
        dry = await apiFetch('/admin/resummarise-legacy?all_active=true&dry_run=true', { method: 'POST' });
      } catch (e) {
        setResummMsg({ text: e.message, ok: false });
        return;
      }
      if (!dry.targets) {
        setResummMsg({ text: dry.message, ok: true });
        return;
      }
      if (!window.confirm(
        `Re-summarise ALL ${dry.targets.toLocaleString()} active tenders?\n\n`
        + 'One AI call per notice, and every tender vector is rewritten. Use this after '
        + 'changing the summariser prompt — a notice that summarised successfully under '
        + 'the old prompt is unreachable any other way.\n\n'
        + 'The follow-up fan-out resets every match Claude has already adjudicated '
        + '(promoted / rejected) back to pending, and invalidates the verdict cache. '
        + 'Both will have to be paid for again.'
      )) return;
    }

    setResummBusy(true);
    setResummMsg(null);
    setResummLeft(null);
    try {
      const url = `/admin/resummarise-legacy${allActive ? '?all_active=true' : ''}`;
      const res = await apiFetch(url, { method: 'POST' });
      if (!res.targets) {
        setResummMsg({ text: res.message, ok: true });
        setResummBusy(false);
        return;
      }
      setResummMsg({ text: res.message, ok: true });
      setResummLeft(res.targets);
      let tries = 0;
      resummPollRef.current = setInterval(async () => {
        tries += 1;
        const st = await apiFetch('/admin/resummarise-legacy/status').catch(() => null);
        const p = st?.progress;
        if (p) setResummLeft(Math.max(0, (p.targets ?? 0) - (p.processed ?? 0)));

        if (p && p.running === false) {
          clearInterval(resummPollRef.current);
          resummPollRef.current = null;
          setResummLeft(null);
          await fetchJobs();
          await trigger('fanout', '/admin/run-fanout'); // refresh matches from the new vectors
          setResummMsg({
            text: p.failed_batches
              ? `Re-summarise finished with ${p.failed_batches} failed batch(es) — those rows keep `
                + 'their old summary and nothing records which they are, so re-run it. '
                + 'Fan-out started for the rows that did land.'
              : `Re-summarise complete (${(p.reembedded ?? 0).toLocaleString()} tenders) — `
                + 'fan-out started to refresh matches.',
            ok: !p.failed_batches,
          });
          setResummBusy(false);
        } else if (tries > 900) { // 60 min at 4s — a ceiling, not an expected finish
          clearInterval(resummPollRef.current);
          resummPollRef.current = null;
          setResummBusy(false);
          setResummMsg({
            text: 'Still running after 60 minutes — stopped watching, but the job has NOT been '
              + 'cancelled. Check the Render log, then run fan-out manually once it finishes.',
            ok: false,
          });
        }
      }, 4000);
    } catch (e) {
      setResummMsg({ text: e.message, ok: false });
      setResummBusy(false);
    }
  }

  // Rebuild every tender vector under the current embed-text format. Like the
  // re-summarise job this has no *_runs row, so progress comes from its own
  // status endpoint. That endpoint reports the LAST COMPLETED run held in the
  // backend process — which may already be populated from an earlier run — so
  // we remember its timestamp first and wait for a DIFFERENT one, rather than
  // treating "a run exists" as "our run finished".
  async function runReembed() {
    if (!window.confirm(
      'Re-embed ALL tender vectors under the current embed-text format?\n\n'
      + 'No AI calls (embeddings only), so this is cheap and safe to repeat. '
      + 'Afterwards run "Re-embed profiles" and then "Re-match all" — until '
      + 'both sides are rebuilt, new tender vectors are being compared against '
      + 'old profile vectors.'
    )) return;
    setReembedBusy(true);
    setReembedMsg(null);
    try {
      const before = await apiFetch('/admin/reembed-tenders/status').catch(() => null);
      const previous = before?.last_run?.finished_at || null;

      const res = await apiFetch('/admin/reembed-tenders', { method: 'POST' });
      if (!res.active_tenders) {
        setReembedMsg({ text: res.message, ok: true });
        setReembedBusy(false);
        return;
      }
      setReembedMsg({ text: res.message, ok: true });

      let tries = 0;
      reembedPollRef.current = setInterval(async () => {
        tries += 1;
        const st = await apiFetch('/admin/reembed-tenders/status').catch(() => null);
        const run = st?.last_run;
        const done = run && run.finished_at !== previous;
        if (done || tries > 90) {
          clearInterval(reembedPollRef.current);
          reembedPollRef.current = null;
          setReembedBusy(false);
          if (!done) {
            setReembedMsg({ text: 'Still running — check back shortly.', ok: true });
            return;
          }
          // A failed batch is not retried in place (there is no per-row format
          // stamp to re-select on), so the run reports it and re-running is the
          // fix. Say so plainly instead of showing a bare success.
          setReembedMsg(run.failed_batches
            ? { text: `Finished with ${run.failed_batches} failed batch(es) — some vectors are still on the old format. Run it again.`, ok: false }
            : { text: `Re-embedded ${run.reembedded.toLocaleString()} tenders in ${run.seconds}s. Now run "Re-embed profiles", then "Re-match all".`, ok: true });
          await fetchJobs();
        }
      }, 4000);
    } catch (e) {
      setReembedMsg({ text: e.message, ok: false });
      setReembedBusy(false);
    }
  }

  // Re-match the whole corpus against the current profiles (recovers tenders
  // fanned out before a profile existed). Fan-out is tracked in active-jobs, so
  // just kick it off and let the normal poll drive the Fan-out card.
  async function runRematchAll() {
    if (!window.confirm('Re-match ALL tenders against every profile? This re-runs fan-out over the whole corpus (a few minutes).')) return;
    setRematchBusy(true);
    setRematchMsg(null);
    try {
      const res = await apiFetch('/admin/rematch-all-tenders', { method: 'POST' });
      setRematchMsg({ text: res.message, ok: true });
      await fetchJobs();
      startPolling();
    } catch (e) {
      setRematchMsg({ text: e.message, ok: false });
    } finally {
      setRematchBusy(false);
    }
  }

  // Refresh each profile's score distribution (one Pinecone query per profile)
  // plus the CPV coverage table. Runs inline and returns in seconds, so there is
  // no job row to poll — just report the counts it comes back with.
  async function runCalibration() {
    setCalibBusy(true);
    setCalibMsg(null);
    try {
      const res = await apiFetch('/admin/run-calibration', { method: 'POST' });
      setCalibMsg({
        text: `${res.calibrated} of ${res.profiles} profiles calibrated`
          + (res.failed ? `, ${res.failed} failed` : '')
          + (res.skipped ? `, ${res.skipped} skipped` : '')
          + ` · ${res.coverage_prefixes?.toLocaleString()} CPV prefixes · ${res.duration_seconds}s`,
        ok: res.failed === 0,
      });
    } catch (e) {
      setCalibMsg({ text: e.message, ok: false });
    } finally {
      setCalibBusy(false);
    }
  }

  // Add the family node above every CPV code each profile already has. No LLM and
  // no re-embed — pure arithmetic over stored codes, and the matcher recomputes
  // the code relation at read time, so it lands as soon as it is written. Runs
  // inline in seconds, so there is no job row to poll.
  async function closeCpvFamilies(dryRun) {
    setFamBusy(true);
    setFamMsg(null);
    try {
      const res = await apiFetch(
        `/admin/close-cpv-families?dry_run=${dryRun}`, { method: 'POST' });
      setFamResult(res);
      setFamMsg({
        text: `${dryRun ? 'Preview' : 'Applied'} — ${res.profiles_changed} of `
          + `${res.profiles_total} profiles, +${res.codes_added} codes`
          + (res.failed?.length ? `, ${res.failed.length} failed` : ''),
        ok: !res.failed?.length,
      });
    } catch (e) {
      setFamMsg({ text: e.message, ok: false });
    } finally {
      setFamBusy(false);
    }
  }

  // Re-run the onboarding CPV selector over existing profiles. Selection fires at
  // profile CREATION only, so profiles never pick up improvements to it. One LLM
  // call per profile, so it runs in the background and we poll for the diff.
  //
  // Dry run costs exactly the same as a real run — the diff IS the product, so
  // check it before writing.
  async function runCpvReselect(mode, dryRun) {
    setCpvBusy(true);
    setCpvMsg(null);
    try {
      const res = await apiFetch(
        `/admin/reselect-cpv?mode=${mode}&dry_run=${dryRun}`, { method: 'POST' });
      setCpvMsg({ text: res.message, ok: true });
      pollCpv();
    } catch (e) {
      setCpvMsg({ text: e.message, ok: false });
      setCpvBusy(false);
    }
  }

  function pollCpv() {
    clearInterval(cpvPollRef.current);
    cpvPollRef.current = setInterval(async () => {
      try {
        const st = await apiFetch('/admin/reselect-cpv/status');
        setCpvStatus(st);
        if (!st?.running) {
          clearInterval(cpvPollRef.current);
          setCpvBusy(false);
        }
      } catch {
        clearInterval(cpvPollRef.current);
        setCpvBusy(false);
      }
    }, 3000);
  }

  // Word matching (Lane A) only fills in a company's and a notice's word lists
  // when they are saved or ingested, so everything already in the system has
  // none and the lane does nothing until this has run. No AI and no re-embedding
  // — it re-reads text that is already stored — but it is one write per row, so
  // it runs in the background and we poll.
  //
  // Split by scope on purpose: the tender half has to wait for the re-summary
  // sweep (that rewrites the summaries this reads), the profile half does not.
  async function backfillLexical(scope, dryRun) {
    setLexBusy(true);
    setLexMsg(null);
    try {
      const res = await apiFetch(
        `/admin/backfill-lexical-terms?scope=${scope}&dry_run=${dryRun}`, { method: 'POST' });
      setLexMsg({ text: res.message, ok: true });
      if (res.targets?.total) pollLexical();
      else setLexBusy(false);
    } catch (e) {
      setLexMsg({ text: e.message, ok: false });
      setLexBusy(false);
    }
  }

  function pollLexical() {
    clearInterval(lexPollRef.current);
    lexPollRef.current = setInterval(async () => {
      try {
        const st = await apiFetch('/admin/backfill-lexical-terms/status');
        setLexStatus(st);
        if (!st?.running) {
          clearInterval(lexPollRef.current);
          setLexBusy(false);
        }
      } catch {
        clearInterval(lexPollRef.current);
        setLexBusy(false);
      }
    }, 3000);
  }

  // Which profiles can produce trustworthy matches. Read-only; unhealthy ones are
  // withheld from the AUTO tier, so this is the list to work through when a
  // customer reports seeing nothing.
  async function loadHealth() {
    setHealthBusy(true);
    setHealthErr(null);
    try {
      setHealth(await apiFetch('/admin/profile-health'));
    } catch (e) {
      setHealthErr(e.message);
    } finally {
      setHealthBusy(false);
    }
  }

  // Cost is shown before it's spent: preview counts candidates, run adjudicates.
  async function loadPromoPreview() {
    setPromoMsg(null);
    try {
      setPromoPreview(await apiFetch('/admin/promotion-preview'));
    } catch (e) {
      setPromoMsg({ text: e.message, ok: false });
    }
  }

  async function runPromotion() {
    if (!window.confirm(
      `Send ${promoPreview?.candidates ?? 'the'} uncertain match(es) to Claude for review? `
      + 'Approved ones become visible to customers; rejected ones stay hidden. '
      + 'Nothing customers already see is affected.'
    )) return;
    setPromoBusy(true);
    setPromoMsg(null);
    try {
      const res = await apiFetch('/admin/run-promotion', { method: 'POST' });
      setPromoMsg({ text: res.message, ok: true });
      await fetchJobs();
      startPolling();
    } catch (e) {
      setPromoMsg({ text: e.message, ok: false });
    } finally {
      setPromoBusy(false);
    }
  }

  const stats           = jobs?.stats || {};
  const fanoutRunning   = jobs?.fanout?.status   === 'running';
  const verdictRunning  = jobs?.verdict?.status  === 'running';
  const catchupBusy     = fanoutRunning || verdictRunning;

  const countryLabel = selectedCountries.length === EU_COUNTRIES.length
    ? 'all countries'
    : selectedCountries.length === 0
      ? 'no countries'
      : selectedCountries.length === 1
        ? (EU_COUNTRIES.find(x => x.code === selectedCountries[0])?.name ?? selectedCountries[0])
        : `${selectedCountries.length} countries`;

  return (
    <div style={c.page}>
      {/* Header */}
      <div style={c.headerRow}>
        <h1 style={c.heading}>Pipeline Control</h1>
        <div style={c.liveRow}>
          <span style={c.liveDot} />
          {lastRefresh ? `Updated ${timeSince(lastRefresh)}` : 'Connecting…'}
        </div>
      </div>
      <p style={c.sub}>Ingest from TED, fan-out to profiles, re-embed, and rank — all independently triggerable.</p>

      {/* Stats bar */}
      <div style={c.statsBar}>
        {[
          ['Tenders in DB',     stats.total_tenders,     null],
          ['Embedded',          stats.embedded_tenders,   null],
          ['Awaiting fan-out',  stats.awaiting_fanout,    null],
          ['Pending matches',   stats.pending_matches,   null],
          ['Dropped by Claude', stats.dropped_verdicts,   stats.dropped_verdicts > 0 ? '#f97316' : null],
          ['Failed',            stats.failed_verdicts,    stats.failed_verdicts   > 0 ? '#ef4444' : null],
          ['Overflow',          stats.overflow_verdicts,  stats.overflow_verdicts > 0 ? '#a855f7' : null],
          ['Verdicts',          stats.total_verdicts,     null],
          ['Active profiles',   stats.active_profiles,    null],
          ['Users',             stats.total_users,        null],
          ['Expired tenders',   stats.expired_tenders,    stats.expired_tenders  > 0 ? '#64748b' : null],
          ['Summary failures',  stats.failed_summaries,   stats.failed_summaries > 0 ? '#ef4444' : null],
        ].map(([label, val, color]) => (
          <div key={label} style={c.stat}>
            <div style={c.statLabel}>{label}</div>
            <div style={{ ...c.statVal, ...(color ? { color } : {}) }}>{val?.toLocaleString() ?? '—'}</div>
          </div>
        ))}
      </div>

      {/* Flow strip + quick action */}
      <div style={c.flowStrip}>
        {[
          ['① Ingestion', jobs?.ingestion?.status],
          ['② Fan-out',   jobs?.fanout?.status],
          ['③ Embedding', jobs?.embedding?.status],
          ['④ Verdicts',  jobs?.verdict?.status],
        ].map(([label, status], i) => (
          <React.Fragment key={label}>
            <div style={c.flowStep(status || 'idle')}>
              <span style={c.flowDot(status || 'idle')} />
              {label}
            </div>
            {i < 3 && <span style={c.flowArrow}>→</span>}
          </React.Fragment>
        ))}
        <div style={c.flexSpacer} />
        <button
          style={c.btn('green', catchupBusy)}
          onClick={runCatchup}
          disabled={catchupBusy}
          title="Trigger fan-out + verdicts simultaneously"
        >
          Run fan-out + verdicts
        </button>
      </div>

      {/* Pipeline cards */}
      <div style={c.grid}>

        {/* ① Ingestion */}
        <PipelineCard
          number="① Ingestion"
          title="TED Ingest"
          description="TED API → translate → embed → Supabase + Pinecone"
          job={jobs?.ingestion}
          pendingCount={null}
          onRun={triggerIngestion}
          runLabel={`Run — ${countryLabel}, ${maxTenders || '∞'} tenders`}
          message={msgs.ingestion}
        >
          <div style={c.configRow}>
            <span style={c.configLabel}>Days back</span>
            <input
              type="number" min="1" max="90" value={daysBack}
              onChange={e => { setDaysBack(Number(e.target.value)); setTedResult(null); }}
              style={c.numInput}
            />
            <span style={{ ...c.configLabel, marginLeft: 12, minWidth: 'auto' }}>Max tenders</span>
            <input
              type="number" min="0" max="1000" value={maxTenders}
              onChange={e => setMaxTenders(Number(e.target.value))}
              style={c.numInput}
            />
            <span style={c.hint}>0 = unlimited</span>
          </div>

          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 4 }}>
              <span style={c.configLabel}>Countries</span>
              <button style={c.textBtn} onClick={() => setCountriesOpen(o => !o)}>{countriesOpen ? 'Hide' : 'Show'}</button>
              <button style={c.textBtn} onClick={() => { setSelectedCountries(EU_COUNTRIES.map(x => x.code)); setTedResult(null); }}>Select all</button>
              <button style={c.textBtn} onClick={() => { setSelectedCountries([]); setTedResult(null); }}>Unselect all</button>
            </div>
            {countriesOpen && (
              <div style={c.countryGrid}>
                {EU_COUNTRIES.map(({ code, name }) => (
                  <div
                    key={code}
                    style={c.chip(selectedCountries.includes(code))}
                    onClick={() => {
                      setSelectedCountries(prev =>
                        prev.includes(code) ? prev.filter(x => x !== code) : [...prev, code]
                      );
                      setTedResult(null);
                    }}
                  >
                    <span style={{ fontWeight: 700 }}>{code}</span>
                    <span style={{ opacity: 0.7 }}>{name}</span>
                  </div>
                ))}
              </div>
            )}
            <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 4, marginBottom: 10 }}>
              {selectedCountries.length === EU_COUNTRIES.length
                ? 'All TED countries'
                : selectedCountries.length === 0
                  ? 'No countries selected'
                  : `${selectedCountries.length} selected: ${selectedCountries.join(', ')}`}
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <button style={c.btn('outline', tedChecking)} onClick={checkTed} disabled={tedChecking}>
              {tedChecking ? 'Checking…' : 'Check TED availability'}
            </button>
            {tedResult && (
              <span style={c.tedResult(!tedResult.error)}>
                {tedResult.error
                  ? `Error: ${tedResult.error}`
                  : `${tedResult.total.toLocaleString()} notices available`}
              </span>
            )}
          </div>
        </PipelineCard>

        {/* ② Fan-out */}
        <PipelineCard
          number="② Fan-out"
          title="Fan-out"
          description="Embedded tenders → Stage A+B gate (deadline · country · CPV · multi-vector consensus) → pending verdicts"
          job={jobs?.fanout}
          pendingCount={jobs ? (stats.awaiting_fanout ?? 0) : null}
          countLabel="tenders awaiting fan-out"
          onRun={() => trigger('fanout', '/admin/run-fanout')}
          runLabel="Run fan-out"
          message={msgs.fanout}
        />

        {/* ③ Profile Embedding */}
        <PipelineCard
          number="③ Embedding"
          title="Profile Embedding"
          description="Re-embed company profiles + regenerate synthetic variants"
          job={jobs?.embedding}
          pendingCount={jobs ? (stats.active_profiles ?? 0) : null}
          countLabel="active profiles"
          onRun={() => trigger('embedding', '/admin/run-profile-embedding')}
          runLabel="Re-embed profiles"
          message={msgs.embedding}
        />

        {/* ③b CPV table (one-off infra job) */}
        <PipelineCard
          number="③b CPV table"
          title="CPV Table Embedding"
          description="One-off: embed the full CPV 2008 table (9,453 labels) into the Pinecone cpv namespace — powers onboarding CPV suggestions and the full-catalogue search. Idempotent: skips when already populated."
          onRun={() => trigger('cpvTable', '/admin/embed-cpv-table')}
          runLabel="Embed CPV table"
          message={msgs.cpvTable}
        />

        {/* ④ Verdicts */}
        <PipelineCard
          number="④ Verdicts"
          title="Evaluate with Claude"
          description="All pending matches → cache check → Claude categorical stamp (great / good / maybe) → match verdicts"
          job={jobs?.verdict}
          pendingCount={jobs ? (stats.pending_matches ?? 0) : null}
          countLabel="matches pending evaluation"
          onRun={triggerVerdict}
          runLabel={
            bandCount == null
              ? 'Evaluate with Claude'
              : `Evaluate ${bandCount.toLocaleString()} with Claude`
          }
          message={msgs.verdict}
        >
          <div style={{ marginBottom: 8 }}>
            <span style={c.configLabel}>Pinecone score band</span>
          </div>
          <div style={c.sliderRow}>
            <span style={c.sliderTag}>MIN</span>
            <input
              type="range" min="50" max="100" step="1" value={scoreBand.min}
              onChange={e => {
                const v = Number(e.target.value);
                setScoreBand(b => ({ ...b, min: Math.min(v, b.max) }));
              }}
              style={c.range}
            />
            <span style={c.sliderVal}>{scoreBand.min}</span>
          </div>
          <div style={c.sliderRow}>
            <span style={c.sliderTag}>MAX</span>
            <input
              type="range" min="50" max="100" step="1" value={scoreBand.max}
              onChange={e => {
                const v = Number(e.target.value);
                setScoreBand(b => ({ ...b, max: Math.max(v, b.min) }));
              }}
              style={c.range}
            />
            <span style={c.sliderVal}>{scoreBand.max}</span>
          </div>

          <div style={{ ...c.configRow, marginTop: 8, marginBottom: 0 }}>
            <span style={c.configLabel}>Top N / profile</span>
            <input
              type="number" min="1" max="200" value={topN} placeholder="no limit"
              onChange={e => setTopN(e.target.value)}
              style={{ ...c.numInput, width: 88, textAlign: 'left' }}
            />
            <span style={c.hint}>highest-scoring matches per profile</span>
          </div>

          <div style={c.countLine}>
            {countLoading
              ? '→ counting…'
              : <>→ <strong style={{ color: '#0f172a' }}>{(bandCount ?? 0).toLocaleString()}</strong> matches will be evaluated</>}
          </div>
        </PipelineCard>

      </div>

      {/* Dropped verdicts review */}
      {stats.dropped_verdicts > 0 && (
        <DroppedVerdictsPanel count={stats.dropped_verdicts} />
      )}

      {/* Maintenance */}
      <div style={{ ...c.card, marginBottom: 24 }}>
        <div style={c.cardHeader}>
          <div>
            <div style={c.cardTitle}>Maintenance</div>
            <div style={c.cardDesc}>One-off cleanup operations. Safe to run at any time.</div>
          </div>
        </div>
        <div style={c.divider} />
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 2 }}>
              Clean expired tenders
            </div>
            <div style={{ fontSize: 12, color: '#64748b' }}>
              Soft-deletes tenders past their deadline, removes their Pinecone vectors, and clears pending verdicts for them.
              {stats.expired_tenders > 0 && (
                <span style={{ marginLeft: 6, fontWeight: 600, color: '#64748b' }}>
                  ({stats.expired_tenders?.toLocaleString()} eligible)
                </span>
              )}
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
            <button
              style={c.btn('secondary', cleanupBusy)}
              onClick={runCleanup}
              disabled={cleanupBusy || !stats.expired_tenders}
            >
              {cleanupBusy ? 'Cleaning…' : 'Run cleanup'}
            </button>
            {cleanupMsg && <span style={c.msgText(cleanupMsg.ok)}>{cleanupMsg.text}</span>}
          </div>
        </div>

        <div style={c.divider} />
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 2 }}>
              Clean orphaned profile vectors
            </div>
            <div style={{ fontSize: 12, color: '#64748b' }}>
              Removes Pinecone profile vectors whose profile no longer exists (left behind by user deletions or manual DB edits).
              {orphanCount != null && (
                <span style={{ marginLeft: 6, fontWeight: 600, color: orphanCount > 0 ? '#f97316' : '#64748b' }}>
                  ({orphanCount.toLocaleString()} orphaned)
                </span>
              )}
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
            <button
              style={c.btn('secondary', orphanBusy)}
              onClick={runOrphanCleanup}
              disabled={orphanBusy || !orphanCount}
            >
              {orphanBusy ? 'Cleaning…' : 'Run cleanup'}
            </button>
            {orphanMsg && <span style={c.msgText(orphanMsg.ok)}>{orphanMsg.text}</span>}
          </div>
        </div>

        <div style={c.divider} />
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 2 }}>
              Re-summarise tenders
            </div>
            <div style={{ fontSize: 12, color: '#64748b' }}>
              Re-translates, re-summarises and re-embeds tenders, then auto-runs fan-out to refresh
              their matches. Content-free stub notices are skipped. <strong>Retry unprocessed</strong> covers
              rows that never processed or failed at ingest — cheap, run any time. <strong>Re-summarise
              all</strong> covers the whole corpus and is what you run after changing the summariser
              prompt: a notice that summarised successfully under the old prompt is unreachable any
              other way. It costs one AI call per notice and resets already-adjudicated matches.
              <span style={{ marginLeft: 6, fontWeight: 600, color: stats.resummarise_targets > 0 ? '#f59e0b' : '#64748b' }}>
                {(stats.resummarise_targets ?? 0).toLocaleString()} eligible
              </span>
              {' · '}
              <span style={{ fontWeight: 600, color: stats.failed_summaries > 0 ? '#ef4444' : '#64748b' }}>
                {(stats.failed_summaries ?? 0).toLocaleString()} failed to process
              </span>
              {resummLeft != null && (
                <span style={{ marginLeft: 6, fontWeight: 600, color: '#1d4ed8' }}>· {resummLeft.toLocaleString()} left…</span>
              )}
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
            <button
              style={c.btn('secondary', resummBusy || !stats.resummarise_targets)}
              onClick={() => runResummarise(false)}
              disabled={resummBusy || !stats.resummarise_targets}
            >
              {resummBusy ? 'Working…' : 'Retry unprocessed'}
            </button>
            <button
              style={c.btn('secondary', resummBusy)}
              onClick={() => runResummarise(true)}
              disabled={resummBusy}
            >
              Re-summarise all
            </button>
            {resummMsg && <span style={c.msgText(resummMsg.ok)}>{resummMsg.text}</span>}
          </div>
        </div>

        <div style={c.divider} />
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 2 }}>
              Re-embed tenders
            </div>
            <div style={{ fontSize: 12, color: '#64748b' }}>
              Rebuilds every tender vector from the text already stored — no AI calls, so it
              is cheap and safe to repeat. Use after a change to how tender embed text is
              assembled. Always run <strong>Re-embed profiles</strong> after it: until both
              sides are rebuilt, new tender vectors are compared against old profile vectors
              and every score is unreliable.
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
            <button
              style={c.btn('secondary', reembedBusy)}
              onClick={runReembed}
              disabled={reembedBusy}
            >
              {reembedBusy ? 'Working…' : 'Re-embed tenders'}
            </button>
            {reembedMsg && <span style={c.msgText(reembedMsg.ok)}>{reembedMsg.text}</span>}
          </div>
        </div>

        <div style={c.divider} />
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 2 }}>
              Re-match all tenders
            </div>
            <div style={{ fontSize: 12, color: '#64748b' }}>
              Resets fan-out on every active tender and re-runs it against the current profiles.
              Use after adding or rebuilding profiles — a plain &ldquo;Run fan-out&rdquo; only
              touches un-processed tenders, so it can&rsquo;t recover tenders that were ingested
              before a profile existed. Idempotent (upserts matches, never deletes).
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
            <button
              style={c.btn('secondary', rematchBusy)}
              onClick={runRematchAll}
              disabled={rematchBusy}
            >
              {rematchBusy ? 'Starting…' : 'Re-match all'}
            </button>
            {rematchMsg && <span style={c.msgText(rematchMsg.ok)}>{rematchMsg.text}</span>}
          </div>
        </div>

        <div style={c.divider} />
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 2 }}>
              Recalibrate match scores
            </div>
            <div style={{ fontSize: 12, color: '#64748b' }}>
              Measures how each profile scores against the live corpus, so a match can be judged
              unusual <em>for that profile</em> rather than against one global threshold. Runs nightly
              on its own; use this after adding profiles or a large ingestion. Reads only — writes
              nothing but the score stats.
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
            <button
              style={c.btn('secondary', calibBusy)}
              onClick={runCalibration}
              disabled={calibBusy}
            >
              {calibBusy ? 'Measuring…' : 'Recalibrate'}
            </button>
            {calibMsg && <span style={c.msgText(calibMsg.ok)}>{calibMsg.text}</span>}
          </div>
        </div>

        <div style={c.divider} />
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 2 }}>
              Review uncertain matches
            </div>
            <div style={{ fontSize: 12, color: '#64748b' }}>
              Sends only the <strong>borderline</strong> matches to Claude, which decides whether each
              one is worth showing. Approved matches appear to the customer with a
              &ldquo;Reviewed&rdquo; badge and a one-line explanation. This can only <em>add</em>
              matches &mdash; nothing already visible is removed. Check the count first.
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
            <button style={c.btn('secondary', false)} onClick={loadPromoPreview}>
              Check count
            </button>
            <button
              style={c.btn('primary', promoBusy || !promoPreview?.candidates)}
              onClick={runPromotion}
              disabled={promoBusy || !promoPreview?.candidates}
            >
              {promoBusy ? 'Starting…' : 'Review'}
            </button>
            {promoMsg && <span style={c.msgText(promoMsg.ok)}>{promoMsg.text}</span>}
          </div>
        </div>

        {promoPreview && (
          <div style={{
            marginTop: 10, padding: '10px 12px', borderRadius: 8,
            background: '#f8fafc', border: '1px solid #e2e8f0',
          }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#111827' }}>
              {promoPreview.candidates} uncertain match{promoPreview.candidates === 1 ? '' : 'es'} to review
              {promoPreview.skipped_already_shown > 0 && (
                <span style={{ fontWeight: 400, color: '#64748b' }}>
                  {' '}· {promoPreview.skipped_already_shown} skipped (customer already sees the tender)
                </span>
              )}
            </div>
            {Object.keys(promoPreview.per_profile || {}).length > 0 && (
              <div style={{ marginTop: 6, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {Object.entries(promoPreview.per_profile).map(([name, n]) => (
                  <span key={name} style={{
                    fontSize: 12, background: '#eff6ff', color: '#1e40af',
                    padding: '2px 9px', borderRadius: 20,
                  }}>
                    {name} · {n}
                  </span>
                ))}
              </div>
            )}
          </div>
        )}

        <div style={c.divider} />
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 2 }}>
              Broaden CPV codes
            </div>
            <div style={{ fontSize: 12, color: '#64748b' }}>
              Buyers file a notice under whichever code they like, and a profile only counts as
              related if one of its codes sits directly above or below the buyer&rsquo;s on the
              same branch &mdash; two codes off the same fork count as unrelated. This adds the
              fork above every code a profile already has, so its codes cover their neighbours.
              <strong> Only ever adds</strong>, safe to run twice, and takes effect straight away:
              no AI, no re-embedding, no re-matching.
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
            <button
              style={c.btn('secondary', famBusy)}
              onClick={() => closeCpvFamilies(true)}
              disabled={famBusy}
            >
              {famBusy ? 'Working…' : 'Preview'}
            </button>
            <button
              style={c.btn('primary', famBusy)}
              onClick={() => closeCpvFamilies(false)}
              disabled={famBusy}
            >
              Broaden
            </button>
            {famMsg && <span style={c.msgText(famMsg.ok)}>{famMsg.text}</span>}
          </div>
        </div>

        {famResult && (
          <div style={{
            marginTop: 10, padding: '10px 12px', borderRadius: 8,
            background: '#f8fafc', border: '1px solid #e2e8f0',
            maxHeight: 220, overflowY: 'auto',
          }}>
            {(famResult.changes || []).length === 0 ? (
              <div style={{ fontSize: 13, color: '#64748b' }}>
                Nothing to add — every profile already carries its family codes.
              </div>
            ) : (famResult.changes || []).map((ch) => (
              <div key={ch.profile_id} style={{ marginBottom: 8 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#374151' }}>
                  {ch.profile_name}
                  <span style={{ fontWeight: 400, color: '#94a3b8' }}>
                    {' '}{ch.before} → {ch.after}
                  </span>
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginTop: 3 }}>
                  {ch.added.map((a) => (
                    <span key={a.code} style={{
                      fontSize: 11, background: '#ecfdf5', color: '#065f46',
                      padding: '2px 8px', borderRadius: 20,
                    }}>
                      + {a.label || a.code}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        <div style={c.divider} />
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 2 }}>
              Build word lists
            </div>
            <div style={{ fontSize: 12, color: '#64748b' }}>
              Word matching compares the rare words a company uses for what it sells against the
              words a notice actually contains &mdash; it catches specialists that the score and
              the category codes both miss. Word lists are only built when a profile is saved or a
              tender arrives, so <strong>everything already in the system has none</strong> and the
              lane does nothing until this runs. No AI, no re-embedding, safe to run twice.
              <strong> Do tenders after &ldquo;Re-summarise &amp; re-match&rdquo;</strong>, which
              rewrites the summaries this reads. Follow with
              &ldquo;Recalibrate match scores&rdquo; to rebuild the rarity table.
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
            <button
              style={c.btn('secondary', lexBusy)}
              onClick={() => backfillLexical('all', true)}
              disabled={lexBusy}
            >
              {lexBusy ? 'Working…' : 'Preview'}
            </button>
            <button
              style={c.btn('secondary', lexBusy)}
              onClick={() => backfillLexical('profiles', false)}
              disabled={lexBusy}
            >
              Profiles only
            </button>
            <button
              style={c.btn('primary', lexBusy)}
              onClick={() => backfillLexical('all', false)}
              disabled={lexBusy}
            >
              Build all
            </button>
            {lexMsg && <span style={c.msgText(lexMsg.ok)}>{lexMsg.text}</span>}
          </div>
        </div>

        {(lexStatus?.running || lexStatus?.last_run) && (
          <div style={{
            marginTop: 10, padding: '10px 12px', borderRadius: 8,
            background: '#f8fafc', border: '1px solid #e2e8f0', fontSize: 12,
            color: '#475569',
          }}>
            {lexStatus.running ? (
              <span>
                Building{lexStatus.running.dry_run ? ' (preview)' : ''} —{' '}
                {lexStatus.running.done} of {lexStatus.running.total}
              </span>
            ) : (
              <>
                <div style={{ fontWeight: 600, color: '#374151', marginBottom: 4 }}>
                  {lexStatus.last_run.dry_run ? 'Preview' : 'Done'} — term version{' '}
                  {lexStatus.last_run.terms_version}
                </div>
                {['profiles', 'tenders'].map((side) => lexStatus.last_run[side] && (
                  <div key={side}>
                    {side}: {lexStatus.last_run[side].targets} targeted,{' '}
                    {lexStatus.last_run[side].written} written,{' '}
                    {lexStatus.last_run[side].empty_term_rows} produced no words
                    {lexStatus.last_run[side].failed
                      ? `, ${lexStatus.last_run[side].failed} failed` : ''}
                  </div>
                ))}
                {lexStatus.last_run.error && (
                  <div style={{ color: '#b91c1c' }}>{lexStatus.last_run.error}</div>
                )}
              </>
            )}
          </div>
        )}

        <div style={c.divider} />
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 2 }}>
              Refresh CPV codes
            </div>
            <div style={{ fontSize: 12, color: '#64748b' }}>
              Re-runs the onboarding code selector over every profile. Codes decide which tenders a
              customer is <em>eligible</em> to be shown, and they are only ever chosen when a profile
              is first created &mdash; so no existing profile has the current selector&rsquo;s work.
              <strong> Preview first</strong>: it costs the same as running it, and the list of
              changes is the thing worth reading. <em>Add</em> only ever adds codes;
              <em> replace</em> also removes ones the selector no longer picks, including any you
              added by hand.
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
            <button
              style={c.btn('secondary', cpvBusy)}
              onClick={() => runCpvReselect('add', true)}
              disabled={cpvBusy}
            >
              {cpvBusy ? 'Working…' : 'Preview'}
            </button>
            <button
              style={c.btn('primary', cpvBusy)}
              onClick={() => runCpvReselect('add', false)}
              disabled={cpvBusy}
            >
              Add codes
            </button>
            <button
              style={c.btn('secondary', cpvBusy)}
              onClick={() => {
                if (window.confirm(
                  'Replace mode overwrites every profile’s CPV codes with what the selector '
                  + 'picks now. Codes you added by hand will be removed. Continue?')) {
                  runCpvReselect('replace', false);
                }
              }}
              disabled={cpvBusy}
            >
              Replace
            </button>
            {cpvMsg && <span style={c.msgText(cpvMsg.ok)}>{cpvMsg.text}</span>}
          </div>
        </div>

        {(cpvStatus?.running || cpvStatus?.last_run) && (
          <div style={{
            marginTop: 10, padding: '10px 12px', borderRadius: 8,
            background: '#f8fafc', border: '1px solid #e2e8f0',
          }}>
            {cpvStatus.running ? (
              <div style={{ fontSize: 13, color: '#111827' }}>
                {cpvStatus.running.dry_run ? 'Previewing' : 'Updating'} &mdash;{' '}
                {cpvStatus.running.done} of {cpvStatus.running.total} profiles
              </div>
            ) : (
              <>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#111827' }}>
                  {cpvStatus.last_run.dry_run ? 'Preview' : 'Applied'} ·{' '}
                  {cpvStatus.last_run.profiles_changed} of{' '}
                  {cpvStatus.last_run.profiles_considered} profiles changed
                  <span style={{ fontWeight: 400, color: '#64748b' }}>
                    {' '}· +{cpvStatus.last_run.codes_added} codes
                    {cpvStatus.last_run.codes_removed > 0
                      && ` · −${cpvStatus.last_run.codes_removed} removed`}
                    {' '}· {cpvStatus.last_run.duration_seconds}s
                  </span>
                </div>
                {(cpvStatus.last_run.profiles_empty_suggestion > 0
                  || cpvStatus.last_run.profiles_failed > 0) && (
                  <div style={{ fontSize: 12, color: '#b45309', marginTop: 4 }}>
                    {cpvStatus.last_run.profiles_empty_suggestion > 0
                      && `${cpvStatus.last_run.profiles_empty_suggestion} profile(s) returned no codes and were skipped. `}
                    {cpvStatus.last_run.profiles_failed > 0
                      && `${cpvStatus.last_run.profiles_failed} failed to save.`}
                  </div>
                )}
                <div style={{ marginTop: 8, maxHeight: 220, overflowY: 'auto' }}>
                  {(cpvStatus.last_run.changes || []).map((ch) => (
                    <div key={ch.profile_id} style={{ marginBottom: 8 }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: '#374151' }}>
                        {ch.profile_name}
                        <span style={{ fontWeight: 400, color: '#94a3b8' }}>
                          {' '}{ch.before} → {ch.after}
                        </span>
                      </div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginTop: 3 }}>
                        {ch.added.map((a) => (
                          <span key={`a${a.code}`} style={{
                            fontSize: 11, background: '#ecfdf5', color: '#065f46',
                            padding: '2px 8px', borderRadius: 20,
                          }}>
                            + {a.label || a.code}
                          </span>
                        ))}
                        {(ch.removed || []).map((r) => (
                          <span key={`r${r.code}`} style={{
                            fontSize: 11, background: '#fef2f2', color: '#991b1b',
                            padding: '2px 8px', borderRadius: 20,
                          }}>
                            − {r.label || r.code}
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        )}

        <div style={c.divider} />
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 2 }}>
              Profile health
            </div>
            <div style={{ fontSize: 12, color: '#64748b' }}>
              Profiles missing the inputs matching needs — no capabilities description, or no
              usable CPV codes. These are withheld from auto-shown matches, so a customer with a
              flagged profile sees nothing until it&rsquo;s filled in. Read-only.
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
            <button
              style={c.btn('secondary', healthBusy)}
              onClick={loadHealth}
              disabled={healthBusy}
            >
              {healthBusy ? 'Checking…' : 'Check profiles'}
            </button>
            {healthErr && <span style={c.msgText(false)}>{healthErr}</span>}
            {health && !healthErr && (
              <span style={c.msgText(health.unhealthy === 0)}>
                {health.unhealthy === 0
                  ? `All ${health.total} profiles OK`
                  : `${health.unhealthy} of ${health.total} need attention`}
              </span>
            )}
          </div>
        </div>

        {health && health.profiles.length > 0 && (
          <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
            {health.profiles.map((p) => (
              <div
                key={p.profile_id}
                style={{
                  border: '1px solid #e2e8f0', borderRadius: 8, padding: '10px 12px',
                  background: p.healthy ? '#fff' : '#fffbeb',
                  borderColor: p.healthy ? '#e2e8f0' : '#fde68a',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 13, fontWeight: 600, color: '#111827' }}>
                    {p.profile_name}
                  </span>
                  <span style={{
                    fontSize: 11, fontWeight: 600, padding: '1px 7px', borderRadius: 20,
                    background: p.healthy ? '#ecfdf5' : '#fef3c7',
                    color:      p.healthy ? '#047857' : '#92400e',
                  }}>
                    {p.healthy ? 'OK' : 'Needs attention'} · {p.score}
                  </span>
                  <span style={{ fontSize: 12, color: '#64748b' }}>
                    {p.cpv_usable}/{p.cpv_declared} usable CPV codes
                    {p.score_coherence != null && ` · ${Math.round(p.score_coherence * 100)}% on-target retrievals`}
                  </span>
                </div>
                {p.issues.length > 0 && (
                  <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
                    {p.issues.map((i) => (
                      <li key={i.code} style={{
                        fontSize: 12, lineHeight: 1.5,
                        color: i.severity === 'blocking' ? '#b45309' : '#64748b',
                        fontWeight: i.severity === 'blocking' ? 500 : 400,
                      }}>
                        {i.message}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>
        )}

        <div style={c.divider} />
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 2 }}>
              Test email digest
            </div>
            <div style={{ fontSize: 12, color: '#64748b' }}>
              Sends a digest preview to <strong>your own email</strong> using the last 30 days of matches
              (verdicted + cosine). Ignores your frequency setting and doesn&rsquo;t affect real users.
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
            <button
              style={c.btn('secondary', digestBusy === 'daily')}
              onClick={() => sendTestDigest('daily')}
              disabled={!!digestBusy}
            >
              {digestBusy === 'daily' ? 'Sending…' : 'Send daily test'}
            </button>
            <button
              style={c.btn('secondary', digestBusy === 'weekly')}
              onClick={() => sendTestDigest('weekly')}
              disabled={!!digestBusy}
            >
              {digestBusy === 'weekly' ? 'Sending…' : 'Send weekly test'}
            </button>
            {digestMsg && <span style={c.msgText(digestMsg.ok)}>{digestMsg.text}</span>}
          </div>
        </div>
      </div>

      {/* Activity feed */}
      <ActivityFeed runs={runs} />
    </div>
  );
}
